import { Link as RouterLink } from '@tanstack/react-router';
import { useWindowScroll } from '@uidotdev/usehooks';
import { motion } from 'motion/react';
import React from 'react';
import { match } from 'ts-pattern';
import TablerIconArrowRight from '~icons/tabler/arrow-right';
import TablerIconMenu2 from '~icons/tabler/menu-2';

import { Container, Drawer, IconButton, Stack } from 'gooey';
import { Img } from '#/components/images';
import { ImgLogo } from '#/components/images/img-logo';
import {
  type ButtonAnchorLinkProps,
  ButtonLink,
  type ButtonLinkProps,
  type ButtonRouterLinkProps,
} from '#/components/links';
import type { Layout } from '#/content';
import { cn } from '#/utils/classnames';
import { isServer } from '#/utils/general';

export type NavbarProps = Layout['navbar'];

export function Navbar({ nav, ctas }: NavbarProps) {
  const [position] = useWindowScroll();
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // const showBlur = (position.y ?? 0) > 64;
  const showBlur = React.useMemo(() => {
    if (isServer()) return false;
    return (position.y ?? 0) > 64;
  }, [position.y]);

  const navLinks = React.useMemo(() => {
    return nav.filter((navLink) => navLink.type === 'link');
  }, [nav]);

  function closeDrawer() {
    setDrawerOpen(false);
  }

  function openDrawer() {
    setDrawerOpen(true);
  }

  return (
    <React.Fragment>
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.33,
          delay: 0,
          ease: 'easeIn',
        }}
        className={cn(
          'w-full fixed top-0 left-0 z-popover',
          showBlur
            ? 'bg-background-default/50 backdrop-blur-lg shadow-md border-b border-solid border-gray-700'
            : 'bg-transparent',
          drawerOpen ? 'hidden' : 'block'
        )}
      >
        <Container>
          <div className='d-navbar px-0'>
            <div className='d-navbar-start'>
              <RouterLink to='/'>
                <ImgLogo
                  logo='contrast'
                  className='max-w-[100px] md:max-w-full'
                  loading='eager'
                  width={136}
                />
              </RouterLink>
            </div>
            <div className='d-navbar-center hidden md:flex'>
              <Stack direction='row' spacing={2}>
                {navLinks.map((link) => (
                  <ButtonLink
                    variant='text'
                    className='text-gray-300 data-[status=active]:bg-background-paper/90 data-[status=active]:text-gray-500'
                    key={link.url}
                    to={link.url}
                  >
                    {link.label}
                  </ButtonLink>
                ))}
              </Stack>
            </div>
            <div className='d-navbar-end gap-x-2'>
              <CallToActionButton
                cta={ctas.signup}
                ctaType='signup'
                className='hidden md:flex'
                onClick={closeDrawer}
              />
              <IconButton onClick={openDrawer} className='md:hidden'>
                <TablerIconMenu2 />
              </IconButton>
            </div>
          </div>
        </Container>
      </motion.header>
      <Drawer
        anchor='right'
        open={drawerOpen}
        onClose={closeDrawer}
        className='w-full bg-background-default z-dialog md:hidden'
        backdrop={false}
      >
        <Container>
          <div className='d-navbar px-0 mb-4'>
            <div className='d-navbar-start'>
              <RouterLink to='/'>
                <ImgLogo logo='contrast' className='max-w-[100px]' width={100} />
              </RouterLink>
            </div>
            <div className='d-navbar-end'>
              <IconButton onClick={closeDrawer}>
                <TablerIconArrowRight />
              </IconButton>
            </div>
          </div>
        </Container>
        <div className='px-4'>
          <Stack direction='column' className='w-full' spacing={1}>
            {/* <CallToActionButton cta={ctas.login} ctaType='login' onClick={closeDrawer} /> */}
            <CallToActionButton cta={ctas.signup} ctaType='signup' onClick={closeDrawer} />
          </Stack>
          <ul className='d-menu d-menu-lg w-full px-0'>
            {navLinks.map((link) => (
              <li key={link.url} className='border-b last:border-b-0 border-solid border-gray-600'>
                <RouterLink className='rounded-none py-3' to={link.url} onClick={closeDrawer}>
                  {link.label}
                </RouterLink>
              </li>
            ))}
          </ul>
        </div>
      </Drawer>
    </React.Fragment>
  );
}

/*
|------------------
| Internals
|------------------
*/

function CallToActionButton<P extends ButtonRouterLinkProps | ButtonAnchorLinkProps>({
  cta,
  ctaType,
  ...props
}: ButtonLinkProps<P> & {
  cta: Layout['navbar']['ctas']['signup'] | Layout['navbar']['ctas']['login'];
  ctaType: 'login' | 'signup';
}) {
  const linkProps = match(ctaType)
    .with('login', () => ({
      href: cta.url,
    }))
    .otherwise(() => ({
      to: cta.url,
    }));

  return (
    // @ts-expect-error - ignore
    <ButtonLink
      {...props}
      {...linkProps}
      size='medium'
      startIcon={match(ctaType)
        .with('signup', () => (
          <Img src='/joggr-guy.png' alt='icon' height={16} width={16} loading='eager' />
        ))
        .otherwise(() => null)}
      variant={match(ctaType)
        .with('login', () => 'outlined' as const)
        .otherwise(() => 'contained' as const)}
    >
      {cta.label}
    </ButtonLink>
  );
}
